<template>
  <v-form ref="form" @submit.prevent="onSubmit">
    <!-- @slot All content form with all inner inputs. Model will be injected for each inputs. -->
    <slot></slot>
  </v-form>
</template>

<script>
import Resource from "../../../mixins/resource";
import set from "lodash/set";

/**
 * Form component which handle resource saving by calling `create` or `update` data provider methods.
 * It's the better place for made heavy usage of any VA inputs.
 * Use injection which allowing unique global v-model for all inputs.
 */
export default {
  mixins: [Resource],
  provide() {
    return {
      formState: this.formState,
    };
  },
  props: {
    /**
     * Current form model being edited. Represent the final data that will be send through your API.
     * @model
     */
    value: {
      type: Object,
      default: () => {},
    },
    /**
     * Id of resource to be edit. If null, then create a new one.
     */
    id: [String, Number],
    /**
     * Explicit item resource object where all properties must be injected into form model.
     */
    item: {
      type: Object,
      default: () => {},
    },
    /**
     * Default route resource action to redirect after saving.
     * @values list, create, show, edit
     */
    redirect: {
      type: String,
      validator: (v) => ["list", "create", "show", "edit"].includes(v),
      default: "list",
    },
    /**
     * Custom redirect route after successful saving.
     * Should be an object that $router.push() accepts
     */
    customRedirectRoute: {
      type: Object,
      default: () => {},
    },
    /**
     * Disable default redirect behavior
     */
    disableRedirect: Boolean,
    /**
     * Data for success toast with redirect link
     * Accepts link text and link route
     */
    successRedirectLink: {
      type: Object,
      default: () => {},
    },
    /**
     * Boolean value, check or all required form fields set in form
     * need it because tinymce fields are not validated by validate() method
     */
    requiredFieldsSet: {
      type: Boolean,
      required: true,
      default: false,
    },
    /**
     * In some cases, we need to mutate the data that is sent in the create/edit request,
     * but keep the formState model as it is, untouched.
     * For example:
     * Let's say we have an input that is of type 'Text', but under some condition, that field has to
     * become an object with multiple properties. We cant just mutate formState, because if we change
     * text property to an object, va-text-input will inevitably throw an error that invalid prop type
     * was passed. We can modify the data that is sent with this function.
     */
    modifyBeforeSend: {
      type: Function,
      default: (data) => {
        return data;
      },
    },
    /**
     * Function to execute before saving starts (before the async request execution)
     */
    beforeSave: {
      type: Function,
      default: () => {},
    },
    /**
     * Function to execute after the saving is done. Covers both successful and failed saving attempt.
     */
    afterSave: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      originalValue: this.value,
      formState: {
        edit: !!this.id,
        item: this.item,
        model: {},
        saving: false,
        errors: {},
        update: ({ source, value }) => {
          let model = { ...this.formState.model };
          /**
           * If no "source" variable is set, then formState.model gets an empty object key with a value set
           * e.g. "": "asperiores-recusandae-qui" (Visible in vue devtools)
           * This causes issues when determining form dirty state
           * (Since we're checking for form value equality, and formState gets empty object key set randomly)
           *
           * (!!!) If some form properties are not sent in request when form is submitted, check if form input components have:
           * 1. Source property passed;
           * 2. Have a v-model set;
           * 3. Value of v-model is updated programatically (via a setter method)
           */
          source && set(model, source, value);

          this.formState.model = model;

          /**
           * Send model update, called after each single input change.
           */
          this.$emit("input", model);
        },
        submit: (redirect) => {
          this.save(redirect);
        },
      },
    };
  },
  watch: {
    item(val) {
      if (!val) {
        this.formState.model = this.originalValue;
      }
      this.formState.item = val;
    },
    value: {
      handler(val) {
        if (val) {
          this.formState.model = val;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    onSubmit() {
      if (this.$refs.form.validate() && this.requiredFieldsSet) {
        if (this.disableRedirect) {
          this.save();
          return;
        }
        this.save(this.redirect);
      } else {
        this.$store.commit("messages/showToast", {
          color: "error",
          // message: "Required fields are missing."
          message: this.$t("va.messages.required_fields_missing"),
        });
        console.error(
          `Form is blocked from submission because required fields are missing.`
        );
        this.$emit("finishedSaving");
      }
    },
    async save(redirect) {
      this.beforeSave();
      /**
       * Set saving to childs.
       */
      this.formState.saving = true;

      let requestData = null;

      try {
        let { data } = this.id
          ? await this.$store.dispatch(`${this.resource}/update`, {
              id: this.id,
              data: this.modifyBeforeSend(
                Object.assign({}, this.formState.model)
              ),
              successRedirectLink: this.successRedirectLink,
            })
          : await this.$store.dispatch(`${this.resource}/create`, {
              data: this.modifyBeforeSend(
                Object.assign({}, this.formState.model)
              ),
              successRedirectLink: this.successRedirectLink,
            });

        requestData = data;

        this.formState.errors = {};

        /**
         * Sent after success saving.
         */
        this.$emit("saved");

        if (this.customRedirectRoute) {
          this.$router.push(this.customRedirectRoute);
          return;
        }

        switch (redirect) {
          case "list":
            this.$router.push({ name: `${this.resource}_list` });
            break;
          case "create":
            // Reset form in case of same route
            this.formState.item = null;
            this.formState.model = this.originalValue;

            this.$router.push({ name: `${this.resource}_create` });
            break;
          case "show":
            this.$router.push({
              name: `${this.resource}_show`,
              params: { id: data.id },
            });
            break;
          case "edit":
            this.$router.push({
              name: `${this.resource}_edit`,
              params: { id: data.id },
            });
            break;
        }
      } catch (e) {
        if (e.errors) {
          this.formState.errors = e.errors;
        } else throw e;
      } finally {
        this.$emit("finishedSaving");
        this.afterSave(requestData);
        this.formState.saving = false;
      }
    },
  },
};
</script>
